import { z } from 'zod';

import { CountryCodeSchema } from '../country-code/country-code';
import { type QuestionType } from '../question-type/question-type';
import {
  type DateValidationSchema,
  type HeightValidationSchema,
  type MultiSelectValidationSchema,
  type NumberValidationSchema,
  type QuestionValidation,
  type TextValidationSchema,
} from '../question-validation/question-validation';

/**
 * Everything an answer may be, once validated.
 *
 * `null` is a first-class value meaning **"answered as blank"** — distinct from
 * a question the member has not reached. The distinction matters for completion
 * (`profile-completion`): a skipped optional question and an unanswered one look
 * the same in a payload but not in a progress bar.
 */
export type AnswerValue = string | number | boolean | string[] | null;

export const AnswerValueSchema: z.ZodType<AnswerValue> = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.array(z.string()),
  z.null(),
]);

/**
 * The parts of a question that decide what its answer may be — the input to
 * {@link answerValueSchema}.
 *
 * A structural subset rather than the whole `ProfileQuestion`, so the schema can
 * be built from a database row, from a seed definition, or from a form the admin
 * builder has not saved yet (the live preview in P10) without any of them
 * constructing a full DTO.
 */
export interface AnswerSchemaSource {
  type: QuestionType;
  validation: QuestionValidation;
  /** The question's option `value` slugs, in order. Empty for non-option types. */
  optionValues: readonly string[];
  isRequired: boolean;
}

/**
 * The platform ceiling on a written answer, by type — the bound staff can lower
 * and never raise.
 *
 * Exported because a client that wants to stop a member at the ceiling (a
 * `maxLength` on the input, a counter under it) would otherwise carry its own
 * copy of the number, and a copy that drifts turns a hard stop into a refusal
 * the member only meets on submit.
 */
export const TEXT_LENGTH_CEILING = { text: 500, textarea: 4_000 } as const;
const MIN_HEIGHT_CM = 50;
const MAX_HEIGHT_CM = 300;
const CALENDAR_DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Builds the Zod schema **one specific question's** answer must satisfy — from
 * its type, its staff-authored validation config and its own option values.
 *
 * This function is why "a wrong-typed answer → 422" is true without a single
 * hand-written check per field: the api validates every submitted answer through
 * it (`AnswerValidationService`), the mobile wizard pre-validates the same way
 * before it ever posts (M4), and the admin builder previews a question with it.
 * One implementation, three consumers — the `hasPermission`/`resolveVisibility`
 * discipline applied to answers (U3).
 *
 * **Required-ness is part of the schema**, not a separate pass, and it means
 * more than "not null": a required question also refuses an answer that is
 * *blank* — an empty string, whitespace, or an empty selection — whether or not
 * staff configured a lower bound. That is the whole point, and it is easy to get
 * wrong: with `minLength` unset, `''` would otherwise pass validation, be
 * stored, count as an answer, and advance the member past the wizard holding
 * nothing. The seeded `civil_registry_id` is exactly that shape — required, with
 * an empty validation blob.
 *
 * An **optional** question still accepts a blank, because "the member said
 * nothing here" is a real, distinct state from "the member has not reached
 * this".
 *
 * **Nothing is coerced** (N7). A `'true'` string for a boolean question and a
 * `'42'` string for a number are refusals, not conversions — a client that sends
 * the wrong type has a bug, and silently repairing it puts a string in a jsonb
 * column that matching (P16) will later compare numerically.
 */
export function answerValueSchema(source: AnswerSchemaSource): z.ZodType<AnswerValue> {
  const present = presentValueSchema(source);

  // An optional question accepts an explicit blank. A required one does not —
  // and this is the ONLY place required-ness is enforced, so a caller cannot
  // build the schema and forget the check.
  if (!source.isRequired) {
    return present.nullable() as z.ZodType<AnswerValue>;
  }

  // A blank is refused HERE, ahead of the type's own rules, so that "you have not
  // answered this" is one refusal for all nine types. Left to the type, the same
  // omission arrived as `invalid_value` on a country, `custom` on a select and
  // `invalid_format` on a date, and every client had to reconstruct "missing"
  // from three unrelated codes. The pipe is what orders it: stage two runs only
  // when something is actually there, so a filled-but-wrong answer still reports
  // what is wrong with it.
  return z
    .unknown()
    .superRefine((value, ctx) => {
      if (!isBlank(value)) {
        return;
      }

      ctx.addIssue({
        // `too_small` rather than a `custom`: it is the code that already means
        // "there is not enough here", it carries no English sentence a client
        // would have to show, and both clients classify it against the value.
        code: 'too_small',
        minimum: 1,
        inclusive: true,
        origin: Array.isArray(value) ? 'array' : 'string',
        message: REQUIRED_ANSWER_MESSAGE,
      });
    })
    .pipe(present) as unknown as z.ZodType<AnswerValue>;
}

/**
 * The sentence a blank required answer carries.
 *
 * English, like every other message in this file, and every client replaces it:
 * the api has no error i18n, so the code is the contract and the sentence is a
 * convenience (see `presentApiError` on mobile, `fieldErrorMessage` on the admin).
 */
export const REQUIRED_ANSWER_MESSAGE = 'This question must be answered.';

/**
 * Whether an answer is **nothing** — the only thing "required" is about.
 *
 * `false` and `0` are answers, which is why this is a shape test and never a
 * truthiness one.
 */
function isBlank(value: unknown): boolean {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === 'string') {
    return value.trim() === '';
  }

  return Array.isArray(value) && value.length === 0;
}

/** The schema for a **present** answer, before nullability is applied. */
function presentValueSchema(source: AnswerSchemaSource): z.ZodType<Exclude<AnswerValue, null>> {
  const { type, validation, optionValues, isRequired } = source;

  switch (type) {
    case 'text':
      return textSchema(
        validation as z.infer<typeof TextValidationSchema>,
        TEXT_LENGTH_CEILING.text,
        isRequired,
      );

    case 'textarea':
      return textSchema(
        validation as z.infer<typeof TextValidationSchema>,
        TEXT_LENGTH_CEILING.textarea,
        isRequired,
      );

    case 'number':
      return numberSchema(validation as z.infer<typeof NumberValidationSchema>);

    case 'date':
      return dateSchema(validation as z.infer<typeof DateValidationSchema>);

    case 'boolean':
      // Strictly a boolean: `z.coerce.boolean()` reads the string `'false'` as
      // true, which N7 bans outright for exactly this reason.
      return z.boolean();

    case 'single_select':
      return optionValueSchema(optionValues);

    case 'multi_select':
      return multiSelectSchema(
        validation as z.infer<typeof MultiSelectValidationSchema>,
        optionValues,
        isRequired,
      );

    case 'height':
      return heightSchema(validation as z.infer<typeof HeightValidationSchema>);

    case 'country':
      return CountryCodeSchema as unknown as z.ZodType<string>;
  }
}

function textSchema(
  validation: z.infer<typeof TextValidationSchema>,
  ceiling: number,
  isRequired: boolean,
): z.ZodType<string> {
  // Trim FIRST, so every bound below is measured on what will actually be
  // stored. Without it a required question is satisfied by three spaces.
  let schema = z.string().trim();

  // A required question implies a floor of 1 even when staff set none: the
  // alternative is that `''` satisfies it. A configured `minLength` still wins
  // when it is stricter — this raises the floor, it never lowers one.
  const min = isRequired ? Math.max(validation.minLength ?? 1, 1) : validation.minLength;
  const max = Math.min(validation.maxLength ?? ceiling, ceiling);

  if (min !== undefined) {
    schema = schema.min(min);
  }
  schema = schema.max(max);

  const { pattern } = validation;
  if (pattern !== undefined) {
    schema = schema.refine(
      (value) => {
        try {
          return new RegExp(pattern, 'u').test(value);
        } catch {
          // A pattern that no longer compiles (hand-edited row, or a rule saved
          // before the builder's compile check existed) refuses the answer
          // rather than throwing a 500 into a member's submission. The builder
          // rejects such a pattern at write time; this is the fail-closed floor.
          return false;
        }
      },
      { message: 'The answer does not match the required format.' },
    );
  }

  return schema;
}

function numberSchema(validation: z.infer<typeof NumberValidationSchema>): z.ZodType<number> {
  // `.finite()` matters because JSON can carry neither NaN nor Infinity but a
  // server-side caller can, and an infinite value would survive into jsonb.
  let schema = z.number().finite();

  if (validation.integer === true) {
    schema = schema.int();
  }
  if (validation.min !== undefined) {
    schema = schema.min(validation.min);
  }
  if (validation.max !== undefined) {
    schema = schema.max(validation.max);
  }

  return schema;
}

function dateSchema(validation: z.infer<typeof DateValidationSchema>): z.ZodType<string> {
  let schema = z
    .string()
    .regex(CALENDAR_DAY, 'Expected a YYYY-MM-DD date.')
    // A well-formed string is not a real day: `2025-02-30` matches the pattern.
    // Parsed as UTC and read back, so the check cannot drift with the host zone.
    .refine(isRealCalendarDay, { message: 'That date does not exist.' });

  const { minDate, maxDate } = validation;

  if (minDate !== undefined) {
    // Lexicographic comparison is exact for zero-padded `YYYY-MM-DD`, which is
    // why the format is enforced rather than merely suggested.
    schema = schema.refine((value) => value >= minDate, {
      message: `The date must not be earlier than ${minDate}.`,
    });
  }
  if (maxDate !== undefined) {
    schema = schema.refine((value) => value <= maxDate, {
      message: `The date must not be later than ${maxDate}.`,
    });
  }

  return schema;
}

function heightSchema(validation: z.infer<typeof HeightValidationSchema>): z.ZodType<number> {
  // Centimetres, always. The platform-wide range is the floor and ceiling even
  // when staff configure a narrower one, so no configuration can admit a height
  // that matching (P16) would treat as noise.
  const min = Math.max(validation.minCm ?? MIN_HEIGHT_CM, MIN_HEIGHT_CM);
  const max = Math.min(validation.maxCm ?? MAX_HEIGHT_CM, MAX_HEIGHT_CM);

  return z.number().int().min(min).max(max);
}

function optionValueSchema(optionValues: readonly string[]): z.ZodType<string> {
  const allowed: ReadonlySet<string> = new Set(optionValues);

  // Built from a Set rather than `z.enum(...)`: the values come from database
  // rows, so the tuple `z.enum` needs cannot exist at compile time, and an
  // option-backed question with no options must refuse every answer rather than
  // throw while constructing its own validator.
  return z.string().refine((value) => allowed.has(value), {
    message: 'That is not one of this question’s options.',
  });
}

function multiSelectSchema(
  validation: z.infer<typeof MultiSelectValidationSchema>,
  optionValues: readonly string[],
  isRequired: boolean,
): z.ZodType<string[]> {
  let schema = z
    .array(optionValueSchema(optionValues))
    // Duplicates are a client bug, and stored unchecked they would make
    // "how many did she choose" disagree with what she sees on her own profile.
    .refine((values) => new Set(values).size === values.length, {
      message: 'The same option was chosen more than once.',
    }) as unknown as z.ZodArray<z.ZodType<string>>;

  // Required means at least one chosen, even with no configured floor — the
  // array counterpart of the empty-string case above.
  const minSelections = isRequired
    ? Math.max(validation.minSelections ?? 1, 1)
    : validation.minSelections;

  if (minSelections !== undefined) {
    schema = schema.min(minSelections);
  }
  if (validation.maxSelections !== undefined) {
    schema = schema.max(validation.maxSelections);
  }

  return schema as unknown as z.ZodType<string[]>;
}

/**
 * Whether a `YYYY-MM-DD` string names a day that exists.
 *
 * Built and read back in **UTC** (`Date.UTC`), never through the local-time
 * constructor: a local-midnight `Date` in a negative-offset zone reads back as
 * the previous day, which would reject the first of every month for anyone west
 * of Greenwich.
 */
function isRealCalendarDay(value: string): boolean {
  const [year, month, day] = value.split('-').map(Number);

  if (year === undefined || month === undefined || day === undefined) {
    return false;
  }

  const asUtc = new Date(Date.UTC(year, month - 1, day));

  return (
    asUtc.getUTCFullYear() === year &&
    asUtc.getUTCMonth() === month - 1 &&
    asUtc.getUTCDate() === day
  );
}
